import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { ScoreTracker } from '../components/ScoreTracker';
import { StudyCard } from '../components/FlashcardFlip';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, RotateCcw, Check, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export default function LearnModePage() {
    const { setId } = useParams();
    const navigate = useNavigate();
    const inputRef = useRef(null);

    const [flashcardSet, setFlashcardSet] = useState(null);
    const [cards, setCards] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [showMode, setShowMode] = useState('definition'); // 'definition' shows def, user types term
    const [result, setResult] = useState(null); // null, 'correct', 'typo', 'incorrect'
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);

    const [score, setScore] = useState({ correct: 0, incorrect: 0, typos: 0 });
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        fetchSet();
    }, [setId]);

    useEffect(() => {
        if (!result && inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentIndex, result]);

    const fetchSet = async () => {
        try {
            const response = await axios.get(`${API}/sets/${setId}`);
            setFlashcardSet(response.data);
            setCards(shuffleArray(response.data.flashcards));
        } catch (err) {
            console.error('Failed to load set:', err);
        } finally {
            setLoading(false);
        }
    };

    const currentCard = cards[currentIndex];

    const checkAnswer = async () => {
        if (!answer.trim() || checking) return;
        setChecking(true);

        const correctAnswer = showMode === 'definition' ? currentCard.term : currentCard.definition;

        try {
            const response = await axios.post(`${API}/study/check-answer`, {
                user_answer: answer,
                correct_answer: correctAnswer
            });

            const { is_correct, is_typo } = response.data;

            if (is_correct && is_typo) {
                setResult('typo');
                setScore(prev => ({ ...prev, correct: prev.correct + 1, typos: prev.typos + 1 }));
            } else if (is_correct) {
                setResult('correct');
                setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
            } else {
                setResult('incorrect');
                setScore(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
            }
        } catch (err) {
            console.error('Failed to check answer:', err);
        } finally {
            setChecking(false);
        }
    };

    const nextCard = () => {
        if (currentIndex >= cards.length - 1) {
            setCompleted(true);
        } else {
            setCurrentIndex(currentIndex + 1);
            setAnswer('');
            setResult(null);
        }
    };

    const restart = () => {
        setCards(shuffleArray(flashcardSet.flashcards));
        setCurrentIndex(0);
        setAnswer('');
        setResult(null);
        setScore({ correct: 0, incorrect: 0, typos: 0 });
        setCompleted(false);
    };

    const toggleMode = () => {
        setShowMode(showMode === 'definition' ? 'term' : 'definition');
        restart();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (result) {
                nextCard();
            } else {
                checkAnswer();
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex items-center justify-center min-h-[80vh]">
                    <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
            </div>
        );
    }

    if (completed) {
        const totalAnswered = score.correct + score.incorrect;
        const percentage = Math.round((score.correct / totalAnswered) * 100);

        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="pt-24 pb-16 px-6">
                    <div className="max-w-2xl mx-auto study-centered">
                        <div 
                            data-testid="completion-screen"
                            className="text-center"
                        >
                            <h1 className="font-serif text-4xl tracking-tight mb-4">
                                Session Complete!
                            </h1>
                            <p className="font-mono text-6xl text-primary mb-8">
                                {percentage}%
                            </p>

                            <div className="flex justify-center gap-8 mb-12">
                                <div className="text-center">
                                    <p className="font-mono text-2xl text-green-600">{score.correct}</p>
                                    <p className="text-sm text-muted-foreground">Correct</p>
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-2xl text-red-600">{score.incorrect}</p>
                                    <p className="text-sm text-muted-foreground">Incorrect</p>
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-2xl text-yellow-600">{score.typos}</p>
                                    <p className="text-sm text-muted-foreground">Typos</p>
                                </div>
                            </div>

                            <div className="flex justify-center gap-4">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/study/${setId}`)}
                                    data-testid="back-to-modes-button"
                                    className="rounded-none"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Study Modes
                                </Button>
                                <Button
                                    onClick={restart}
                                    data-testid="restart-button"
                                    className="rounded-none"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const displayText = showMode === 'definition' ? currentCard?.definition : currentCard?.term;
    const correctAnswer = showMode === 'definition' ? currentCard?.term : currentCard?.definition;
    const promptLabel = showMode === 'definition' ? 'DEFINITION' : 'TERM';
    const answerLabel = showMode === 'definition' ? 'Type the term' : 'Type the definition';

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <ScoreTracker 
                correct={score.correct}
                incorrect={score.incorrect}
                typos={score.typos}
                total={cards.length}
                current={currentIndex + 1}
            />

            <main className="pt-8 pb-16 px-6">
                <div className="max-w-2xl mx-auto">
                    {/* Controls */}
                    <div className="flex items-center justify-between mb-8">
                        <Button
                            variant="ghost"
                            onClick={() => navigate(`/study/${setId}`)}
                            data-testid="back-button"
                            className="rounded-none"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Back
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={toggleMode}
                            data-testid="toggle-mode-button"
                            className="rounded-none text-xs"
                        >
                            Switch: See {showMode === 'definition' ? 'Term' : 'Definition'}
                        </Button>
                    </div>

                    {/* Card Display */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentIndex}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="mb-8"
                        >
                            <StudyCard 
                                variant={result === 'correct' ? 'success' : result === 'incorrect' ? 'error' : result === 'typo' ? 'warning' : 'default'}
                            >
                                <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-4">
                                    {promptLabel}
                                </span>
                                <p 
                                    data-testid="card-display-text"
                                    className={`font-${showMode === 'term' ? 'serif text-2xl' : 'sans text-xl'} text-center leading-relaxed`}
                                >
                                    {displayText}
                                </p>
                            </StudyCard>
                        </motion.div>
                    </AnimatePresence>

                    {/* Answer Input / Result */}
                    <div className="space-y-4">
                        {!result ? (
                            <>
                                <label className="font-mono text-xs tracking-wider uppercase text-muted-foreground">
                                    {answerLabel}
                                </label>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={answer}
                                    onChange={(e) => setAnswer(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Type your answer..."
                                    data-testid="answer-input"
                                    className="w-full text-lg bg-transparent border-0 border-b-2 border-border focus:border-primary focus:outline-none pb-3 placeholder:text-muted-foreground/50"
                                    autoFocus
                                />
                                <Button
                                    onClick={checkAnswer}
                                    disabled={!answer.trim() || checking}
                                    data-testid="check-answer-button"
                                    className="w-full rounded-none h-12"
                                >
                                    {checking ? 'Checking...' : 'Check Answer'}
                                </Button>
                            </>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="space-y-4"
                            >
                                <div 
                                    data-testid="result-display"
                                    className={`p-6 border ${
                                        result === 'correct' ? 'bg-green-500/10 border-green-500/30' :
                                        result === 'typo' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                        'bg-red-500/10 border-red-500/30'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        {result === 'correct' && <Check className="w-5 h-5 text-green-600" strokeWidth={2} />}
                                        {result === 'typo' && <AlertCircle className="w-5 h-5 text-yellow-600" strokeWidth={2} />}
                                        {result === 'incorrect' && <X className="w-5 h-5 text-red-600" strokeWidth={2} />}
                                        <span className={`font-mono text-sm uppercase tracking-wider ${
                                            result === 'correct' ? 'text-green-600' :
                                            result === 'typo' ? 'text-yellow-600' :
                                            'text-red-600'
                                        }`}>
                                            {result === 'correct' ? 'Correct!' :
                                             result === 'typo' ? 'Almost! (typo accepted)' :
                                             'Incorrect'}
                                        </span>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <p className="text-sm text-muted-foreground">Your answer:</p>
                                        <p className={`font-sans ${result === 'incorrect' ? 'line-through text-muted-foreground' : ''}`}>
                                            {answer}
                                        </p>
                                    </div>

                                    {result === 'incorrect' && (
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <p className="text-sm text-muted-foreground">Correct answer:</p>
                                            <p className="font-sans text-foreground">{correctAnswer}</p>
                                        </div>
                                    )}
                                </div>

                                <Button
                                    onClick={nextCard}
                                    data-testid="next-card-button"
                                    className="w-full rounded-none h-12"
                                >
                                    {currentIndex >= cards.length - 1 ? 'See Results' : 'Next Card'}
                                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                                </Button>
                            </motion.div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
